import { forwardRef, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { cn } from './ui';

export const CODE_LENGTH = 6;

/** The digits of what was typed or pasted, at most six: `123 456` and `123-456` are both a code. */
export function codeDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, CODE_LENGTH);
}

/**
 * A texted code in six boxes (Track ORX), as the desktop's staff sign-in shows its code.
 *
 * One real input lies over the six boxes and takes the typing, so a paste fills every box at once,
 * a phone offers the code from the text it just received (`one-time-code`), and a screen reader
 * hears a single field with its label, hint and error. The boxes are only its picture. No
 * `maxLength`: a pasted "Your code is 123456" would be cut before its digits were reached.
 */
export const CodeBoxes = forwardRef<
  HTMLInputElement,
  {
    id: string;
    label: string;
    value: string;
    onChange: (next: string) => void;
    hint?: string;
    error?: string | null;
  }
>(function CodeBoxes({ id, label, value, onChange, hint, error }, ref) {
  const [focused, setFocused] = useState(false);
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className="space-y-2">
      <label htmlFor={id} className="block text-base font-semibold text-heading">
        {label}
      </label>
      {hint && (
        <p id={hintId} className="text-sm text-muted-foreground">
          {hint}
        </p>
      )}
      <div className="relative max-w-sm">
        <div className="flex justify-between gap-2" aria-hidden="true">
          {Array.from({ length: CODE_LENGTH }, (_, index) => {
            const active = focused && index === Math.min(value.length, CODE_LENGTH - 1);
            return (
              <span
                key={index}
                data-code-box=""
                className={cn(
                  'flex h-14 w-12 items-center justify-center rounded-lg border-2 bg-card text-2xl font-semibold tabular-nums text-heading transition-colors',
                  error ? 'border-danger' : active ? 'border-primary' : 'border-input',
                  active && 'outline outline-2 outline-offset-2 outline-ring',
                )}
              >
                {value[index] ?? ''}
              </span>
            );
          })}
        </div>
        <input
          ref={ref}
          id={id}
          value={value}
          onChange={(event) => onChange(codeDigits(event.target.value))}
          onPaste={(event) => {
            // A whole code replaces whatever was typed, wherever the caret was.
            const pasted = codeDigits(event.clipboardData.getData('text'));
            if (pasted.length === CODE_LENGTH) {
              event.preventDefault();
              onChange(pasted);
            }
          }}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          inputMode="numeric"
          autoComplete="one-time-code"
          aria-invalid={Boolean(error) || undefined}
          aria-describedby={cn(hintId, errorId) || undefined}
          className="absolute inset-0 h-full w-full cursor-text opacity-0"
        />
      </div>
      {error && (
        // Announced as it appears: focus stays in the boxes, where a changed description is not read.
        <p id={errorId} role="alert" className="flex items-center gap-1.5 text-sm font-medium text-danger">
          <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}
    </div>
  );
});
